import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Grid,
  LinearProgress,
  Divider,
  Tabs,
  Tab,
  Tooltip,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Done as DoneIcon,
  Message as MessageIcon,
  Undo as UndoIcon,
  DeleteSweep as DeleteSweepIcon,
  Restore as RestoreIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiService from '../services/api';
import { AnnouncementPhotoRow } from '../components/AnnouncementPhotoRow';
import { AnnouncementCreatedAtCaption } from '../components/AnnouncementCreatedAtCaption';
import { formatFrenchDateTimeInline } from '../utils/formatDateTimeFr';

interface Match {
  id: number;
  score?: string | number;
  reason?: string | null;
  match_score: string | number;
  match_reason: string | null;
  status: 'pending' | 'confirmed' | 'rejected' | 'resolved';
  created_at: string;
  source_announcement: {
    id: number;
    title: string;
    description: string | null;
    created_at?: string;
    type: 'lost' | 'found';
    images?: string[];
    user: { id: number; first_name: string; last_name: string };
    location: {
      city: string;
      latitude?: string | number | null;
      longitude?: string | number | null;
      full_address?: string | null;
    };
    category: { name: string; default_image_url?: string | null };
  };
  target_announcement: {
    id: number;
    title: string;
    description: string | null;
    created_at?: string;
    type: 'lost' | 'found';
    images?: string[];
    user: { id: number; first_name: string; last_name: string };
    location: {
      city: string;
      latitude?: string | number | null;
      longitude?: string | number | null;
      full_address?: string | null;
    };
    category: { name: string; default_image_url?: string | null };
  };
  has_conversation?: boolean;
  conversation_id?: number | null;
}

const MatchesPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<'active' | 'trash'>('active');
  const [fadingRejectedMatchId, setFadingRejectedMatchId] = useState<number | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [unconfirmDialogOpen, setUnconfirmDialogOpen] = useState(false);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState<Match | null>(null);

  const invalidateAllMatches = () => {
    queryClient.invalidateQueries({ queryKey: ['user-matches'] });
  };

  const { data: activeData, isLoading: loadingActive, error: errorActive } = useQuery({
    queryKey: ['user-matches', 'active'],
    queryFn: () => apiService.getUserMatches({ exclude_status: 'rejected' }),
  });

  const { data: trashData, isLoading: loadingTrash, error: errorTrash } = useQuery({
    queryKey: ['user-matches', 'trash'],
    queryFn: () => apiService.getUserMatches({ status: 'rejected' }),
  });

  const isTrashView = tab === 'trash';
  const matchesData = isTrashView ? trashData : activeData;
  const isLoading = isTrashView ? loadingTrash : loadingActive;
  const error = isTrashView ? errorTrash : errorActive;
  const trashCount = trashData?.pagination?.total ?? trashData?.matches?.length ?? 0;

  const confirmMatchMutation = useMutation({
    mutationFn: (id: number) => apiService.confirmMatch(id),
    onSuccess: () => {
      invalidateAllMatches();
      setConfirmDialogOpen(false);
      setSelectedMatch(null);
    },
  });

  const rejectMatchMutation = useMutation({
    mutationFn: (id: number) => apiService.rejectMatch(id),
    onSuccess: (_data, id) => {
      setRejectDialogOpen(false);
      setSelectedMatch(null);
      setFadingRejectedMatchId(id);
      window.setTimeout(() => {
        invalidateAllMatches();
        setFadingRejectedMatchId(null);
      }, 350);
    },
  });

  const resolveMatchMutation = useMutation({
    mutationFn: (id: number) => apiService.resolveMatch(id),
    onSuccess: () => {
      invalidateAllMatches();
      setResolveDialogOpen(false);
      setSelectedMatch(null);
    },
  });

  const unconfirmMatchMutation = useMutation({
    mutationFn: (id: number) => apiService.unconfirmMatch(id),
    onSuccess: () => {
      invalidateAllMatches();
      setUnconfirmDialogOpen(false);
      setSelectedMatch(null);
    },
  });

  const unrejectMatchMutation = useMutation({
    mutationFn: (id: number) => apiService.unrejectMatch(id),
    onSuccess: () => {
      invalidateAllMatches();
      setRestoreDialogOpen(false);
      setSelectedMatch(null);
    },
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'confirmed':
        return 'success';
      case 'rejected':
        return 'error';
      case 'resolved':
        return 'info';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending':
        return 'En attente';
      case 'confirmed':
        return 'Confirmé';
      case 'rejected':
        return 'Rejeté';
      case 'resolved':
        return 'Résolu';
      default:
        return status;
    }
  };

  const formatMatchReason = (reason: string | null | undefined) => {
    if (!reason?.trim()) {
      return (
        <Typography variant="body2" color="text.secondary">
          Aucune raison spécifiée
        </Typography>
      );
    }
    const parts = reason
      .split(/\s*;\s*|\s*,\s*/)
      .map((r) => r.trim())
      .filter(Boolean);
    if (parts.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          Aucune raison spécifiée
        </Typography>
      );
    }
    return parts.map((r, index) => (
      <Chip key={index} label={r} size="small" color="primary" variant="outlined" sx={{ mr: 1, mb: 1 }} />
    ));
  };

  const pageSx = {
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box' as const,
    px: { xs: 0, sm: 0 },
    py: 0,
  };

  if (isLoading) {
    return (
      <Box sx={pageSx}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={pageSx}>
        <Alert severity="error" sx={{ mb: 3 }}>
          Erreur lors du chargement de vos matchings. Veuillez réessayer.
        </Alert>
      </Box>
    );
  }

  const matches = matchesData?.matches || [];

  return (
    <Box sx={pageSx}>
      <Box sx={{ mb: 4, width: '100%' }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontSize: { xs: '1.35rem', sm: '2rem' } }}>
          Mes matchings
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
          Correspondances entre vos annonces Sherlook et celles des utilisateurs. Les matchings rejetés sont dans la{' '}
          <strong>corbeille</strong>.
        </Typography>
      </Box>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
        variant="fullWidth"
      >
        <Tab label="En cours" value="active" />
        <Tab
          icon={<DeleteSweepIcon fontSize="small" />}
          iconPosition="start"
          label={`Corbeille${trashCount > 0 ? ` (${trashCount})` : ''}`}
          value="trash"
        />
      </Tabs>

      {matches.length === 0 ? (
        <Card sx={{ width: '100%' }}>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h6" gutterBottom>
              {isTrashView ? 'Corbeille vide' : 'Aucun matching trouvé'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isTrashView
                ? 'Aucune correspondance rejetée pour le moment.'
                : 'Les correspondances apparaissent lorsque vos annonces (objets trouvés / perdus) matchent avec des utilisateurs.'}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {matches.map((match) => (
            (() => {
              const sourceIsProviderSide = match.source_announcement.type === 'found';
              const providerAnnouncement = sourceIsProviderSide ? match.source_announcement : match.target_announcement;
              const matchedAnnouncement = sourceIsProviderSide ? match.target_announcement : match.source_announcement;
              const providerLabel = providerAnnouncement.type === 'found' ? 'Trouvée' : 'Perdue';
              const matchedLabel = matchedAnnouncement.type === 'lost' ? 'Perdue' : 'Trouvée';
              const contactAnnouncementId = matchedAnnouncement.id;

              return (
            <Grid item xs={12} key={match.id}>
              <Card
                sx={{
                  mb: 2,
                  opacity: fadingRejectedMatchId === match.id ? 0 : 1,
                  transform: fadingRejectedMatchId === match.id ? 'translateY(-6px)' : 'translateY(0px)',
                  transition: 'opacity 350ms ease, transform 350ms ease',
                  pointerEvents: fadingRejectedMatchId === match.id ? 'none' : undefined,
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                      <Typography variant="h6">Correspondance</Typography>
                      <Chip
                        label={`${Math.round(Number.parseFloat(String(match.match_score ?? match.score ?? 0)))}%`}
                        color="primary"
                        variant="filled"
                      />
                      <Chip label={getStatusLabel(match.status)} color={getStatusColor(match.status) as any} variant="outlined" />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {formatFrenchDateTimeInline(match.created_at)}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                      gap: 2,
                      mb: 2,
                    }}
                  >
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                          Votre annonce partenaire ({providerLabel})
                        </Typography>
                        <AnnouncementPhotoRow
                          images={providerAnnouncement.images}
                          categoryDefaultImageUrl={providerAnnouncement.category?.default_image_url}
                          height={140}
                        />
                        <Typography variant="h6" gutterBottom sx={{ mt: 1 }}>
                          {providerAnnouncement.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {providerAnnouncement.description && providerAnnouncement.description.length > 100
                            ? `${providerAnnouncement.description.substring(0, 100)}...`
                            : providerAnnouncement.description || 'Aucune description'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {providerAnnouncement.category.name} · {providerAnnouncement.location.city}
                        </Typography>
                        <Box sx={{ mt: 0.75 }}>
                          <AnnouncementCreatedAtCaption
                            createdAt={providerAnnouncement.created_at}
                            prefix="Créé le"
                          />
                        </Box>
                      </CardContent>
                    </Card>

                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                          Annonce correspondante ({matchedLabel})
                        </Typography>
                        <AnnouncementPhotoRow
                          images={matchedAnnouncement.images}
                          categoryDefaultImageUrl={matchedAnnouncement.category?.default_image_url}
                          height={140}
                        />
                        <Typography variant="h6" gutterBottom sx={{ mt: 1 }}>
                          {matchedAnnouncement.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {matchedAnnouncement.description && matchedAnnouncement.description.length > 100
                            ? `${matchedAnnouncement.description.substring(0, 100)}...`
                            : matchedAnnouncement.description || 'Aucune description'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                          Par {matchedAnnouncement.user.first_name} {matchedAnnouncement.user.last_name}
                        </Typography>
                        <Box sx={{ mb: 1 }}>
                          <AnnouncementCreatedAtCaption
                            createdAt={matchedAnnouncement.created_at}
                            prefix="Créé le"
                          />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {isTrashView ? (
                            <Tooltip title="Matching dans la corbeille">
                              <span>
                                <Button size="small" startIcon={<MessageIcon />} disabled>
                                  Matching dans la corbeille
                                </Button>
                              </span>
                            </Tooltip>
                          ) : match.has_conversation && match.conversation_id ? (
                            <>
                              <Button size="small" color="primary" startIcon={<MessageIcon />} disabled>
                                Contacter
                              </Button>
                              <Button
                                size="small"
                                color="primary"
                                variant="outlined"
                                startIcon={<MessageIcon />}
                                onClick={() => navigate(`/messages?conversation=${match.conversation_id}`)}
                              >
                                Ouvrir la conversation
                              </Button>
                            </>
                          ) : (
                            <Button
                              size="small"
                              color="primary"
                              startIcon={<MessageIcon />}
                              onClick={() => navigate(`/messages?announcement=${contactAnnouncementId}`)}
                            >
                              Contacter
                            </Button>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Score de correspondance
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(
                        100,
                        Math.max(0, Number.parseFloat(String(match.match_score ?? match.score ?? 0)))
                      )}
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Raisons de correspondance
                    </Typography>
                    <Box>{formatMatchReason(match.reason ?? match.match_reason)}</Box>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  {isTrashView && (
                    <Box sx={{ mt: 2, display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<RestoreIcon />}
                        onClick={() => {
                          setSelectedMatch(match);
                          setRestoreDialogOpen(true);
                        }}
                        disabled={unrejectMatchMutation.isPending}
                      >
                        Remettre en matching
                      </Button>
                    </Box>
                  )}

                  {!isTrashView && match.status === 'pending' && (
                    <Box sx={{ mt: 2, display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                      <Button
                        variant="contained"
                        color="success"
                        startIcon={<CheckCircleIcon />}
                        onClick={() => {
                          setSelectedMatch(match);
                          setConfirmDialogOpen(true);
                        }}
                      >
                        Confirmer
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<CancelIcon />}
                        onClick={() => {
                          setSelectedMatch(match);
                          setRejectDialogOpen(true);
                        }}
                      >
                        Rejeter
                      </Button>
                    </Box>
                  )}

                  {!isTrashView && match.status === 'confirmed' && (
                    <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<DoneIcon />}
                        onClick={() => {
                          setSelectedMatch(match);
                          setResolveDialogOpen(true);
                        }}
                      >
                        Marquer comme résolu
                      </Button>
                      <Button
                        variant="outlined"
                        color="warning"
                        startIcon={<UndoIcon />}
                        onClick={() => {
                          setSelectedMatch(match);
                          setUnconfirmDialogOpen(true);
                        }}
                      >
                        Annuler la confirmation
                      </Button>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
              );
            })()
          ))}
        </Grid>
      )}

      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
        <DialogTitle>Confirmer le matching</DialogTitle>
        <DialogContent>
          <Typography>Confirmer cette correspondance ? L&apos;autre utilisateur sera informé.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>Annuler</Button>
          <Button
            onClick={() => selectedMatch && confirmMatchMutation.mutate(selectedMatch.id)}
            color="success"
            variant="contained"
            disabled={confirmMatchMutation.isPending}
          >
            {confirmMatchMutation.isPending ? '…' : 'Confirmer'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)}>
        <DialogTitle>Rejeter le matching</DialogTitle>
        <DialogContent>
          <Typography>
            Cette correspondance sera placée dans la <strong>corbeille</strong>.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>Annuler</Button>
          <Button
            onClick={() => selectedMatch && rejectMatchMutation.mutate(selectedMatch.id)}
            color="error"
            variant="contained"
            disabled={rejectMatchMutation.isPending}
          >
            Rejeter
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={resolveDialogOpen} onClose={() => setResolveDialogOpen(false)}>
        <DialogTitle>Résoudre le matching</DialogTitle>
        <DialogContent>
          <Typography>Marquer cette correspondance comme résolue ? Les annonces seront marquées résolues.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResolveDialogOpen(false)}>Annuler</Button>
          <Button
            onClick={() => selectedMatch && resolveMatchMutation.mutate(selectedMatch.id)}
            color="primary"
            variant="contained"
            disabled={resolveMatchMutation.isPending}
          >
            Résoudre
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={unconfirmDialogOpen} onClose={() => setUnconfirmDialogOpen(false)}>
        <DialogTitle>Annuler la confirmation</DialogTitle>
        <DialogContent>
          <Typography>Le matching repassera en « En attente ».</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnconfirmDialogOpen(false)}>Fermer</Button>
          <Button
            onClick={() => selectedMatch && unconfirmMatchMutation.mutate(selectedMatch.id)}
            color="warning"
            variant="contained"
            disabled={unconfirmMatchMutation.isPending}
          >
            Annuler la confirmation
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={restoreDialogOpen} onClose={() => setRestoreDialogOpen(false)}>
        <DialogTitle>Remettre en matching</DialogTitle>
        <DialogContent>
          <Typography>La correspondance repassera en statut <strong>En attente</strong>.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreDialogOpen(false)}>Annuler</Button>
          <Button
            onClick={() => selectedMatch && unrejectMatchMutation.mutate(selectedMatch.id)}
            color="primary"
            variant="contained"
            disabled={unrejectMatchMutation.isPending}
          >
            Remettre
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MatchesPage;
